import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

// ─── helpers ──────────────────────────────────────────────────────────────────
export const fmtTime = s =>
  `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;

const AUDIO_FMTS = [
  { mimeType:"audio/webm;codecs=opus", audioBitsPerSecond:128000 },
  { mimeType:"audio/webm",             audioBitsPerSecond:128000 },
  { mimeType:"audio/mp4;codecs=mp4a.40.2", audioBitsPerSecond:128000 },
  { mimeType:"audio/mp4",                  audioBitsPerSecond:128000 },
];
export const bestAudioFmt = () =>
  AUDIO_FMTS.find(f=>{ try{ return MediaRecorder.isTypeSupported(f.mimeType); }catch{ return false; } }) || {};

function TB({ children, bg, col, onClick, disabled=false }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding:"5px 12px", borderRadius:"20px", border:"none",
        cursor:disabled?"not-allowed":"pointer", background:bg, color:col,
        fontSize:"12px", fontWeight:"700", fontFamily:"inherit",
        display:"inline-flex", alignItems:"center", gap:"5px",
        opacity:disabled?0.4:1 }}>
      {children}
    </button>
  );
}

// ─── Screenshot panel — fixed 160×90 thumbnails, scrollable strip ────────────
function ScreenshotThumb({ ss, index, total, onDownload, onRemove }) {
  const url = URL.createObjectURL(ss.file);
  return(
    <div style={{ position:"relative", flexShrink:0, width:"160px", height:"90px",
      borderRadius:"8px", overflow:"hidden",
      outline:"2px solid rgba(255,255,255,.5)",
      boxShadow:"0 2px 10px rgba(0,0,0,.6)",
      background:"#000" }}>
      <img src={url} alt="" style={{ width:"160px", height:"90px",
        objectFit:"cover", display:"block", userSelect:"none",
        WebkitUserDrag:"none", pointerEvents:"none" }}/>
      {/* Bottom label */}
      <div style={{ position:"absolute", bottom:0, left:0, right:0,
        background:"rgba(0,0,0,.72)", padding:"2px 5px",
        display:"flex", alignItems:"center", gap:"3px" }}>
        <span style={{ flex:1, fontSize:"9px", color:"#fff", fontWeight:"600",
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {ss.isBookmark?"🔖 "+ss.bookmarkText:"📸 "+ss.screentime}
        </span>
        <button onClick={()=>onDownload(ss)}
          style={{ background:"none", border:"none", cursor:"pointer",
            color:"#ccc", fontSize:"11px", padding:"1px 2px", lineHeight:1 }}>⬇</button>
        <button onClick={()=>onRemove(index)}
          style={{ background:"none", border:"none", cursor:"pointer",
            color:"#ccc", fontSize:"14px", lineHeight:1, padding:"1px 2px" }}>×</button>
      </div>
      {/* Count badge on first visible */}
    </div>
  );
}

function ScreenshotPanel({ screenshots, onDownloadShot, onRemoveShot }) {
  const [expanded, setExpanded] = useState(false);
  const VISIBLE = 3;
  const total   = screenshots.length;
  // Always show last VISIBLE, newest at top
  const visible = expanded ? screenshots : screenshots.slice(-VISIBLE);

  return(
    <div style={{ position:"absolute", bottom:"44px", right:"10px",
      zIndex:6, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"4px" }}>

      {/* Toggle bar */}
      <button onClick={()=>setExpanded(e=>!e)}
        style={{ background:"rgba(0,0,0,.75)", border:"1px solid rgba(255,255,255,.2)",
          borderRadius:"20px", padding:"3px 10px", cursor:"pointer",
          color:"#fff", fontSize:"10px", fontWeight:"700", display:"flex",
          alignItems:"center", gap:"5px" }}>
        <span>📸 {total}</span>
        <span style={{ fontSize:"9px", color:"#9CA3AF" }}>{expanded?"▼ collapse":"▲ all"}</span>
      </button>

      {/* Thumbnail strip — vertical scroll when expanded */}
      <div style={{ display:"flex", flexDirection:"column-reverse", gap:"5px",
        maxHeight: expanded ? "calc(100vh - 140px)" : `${VISIBLE*100}px`,
        overflowY: expanded ? "auto" : "hidden",
        overflowX:"visible", scrollbarWidth:"none" }}>
        {[...visible].reverse().map((ss,i)=>(
          <ScreenshotThumb key={ss.screentime+i}
            ss={ss} index={screenshots.indexOf(ss)}
            total={total}
            onDownload={onDownloadShot}
            onRemove={onRemoveShot}/>
        ))}
      </div>
    </div>
  );
}

// ─── Screen Capture Overlay (portal) ─────────────────────────────────────────
function ScreenOverlay({
  dur, recording, paused, starting,
  screenshotCount, selArea, isSelecting, recError,
  bookmarkModal, bookmarkLabel, screenshots,
  selectingArea, micMuted, onToggleMic,
  onRec, onPause, onResume, onStop, onExit,
  onScreenshot, onBookmarkOpen, onBookmarkSave,
  onBookmarkCancel, onBookmarkLabelChange,
  user, getChunkBlob,
  onSelectArea, onClearArea, onRemoveShot, onDownloadShot,
  onMouseDown, onMouseMove, onMouseUp,
}) {
  const [txLoading, setTxLoading] = useState(false);
  const [txErr,     setTxErr]     = useState("");

  return createPortal(
    <div style={{ position:"fixed", inset:0, zIndex:950,
      display:"flex", flexDirection:"column", background:"#000", overflow:"hidden" }}>

      {/* flash */}
      <div id="pvFlash" style={{ position:"absolute", inset:0, background:"#fff",
        opacity:0, pointerEvents:"none", zIndex:5, transition:"opacity .15s" }}/>

      {/* bookmark modal */}
      {bookmarkModal && (
        <div style={{ position:"absolute", inset:0, zIndex:20,
          background:"rgba(0,0,0,.65)", display:"flex",
          alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#1a1a1a", border:"1px solid rgba(255,255,255,.2)",
            borderRadius:"12px", padding:"22px", width:"320px" }}>
            <div style={{ fontSize:"15px", fontWeight:"700", color:"#fff", marginBottom:"4px" }}>
              🔖 Bookmark</div>
            <div style={{ fontSize:"11px", color:"#9CA3AF", marginBottom:"12px" }}>
              at {fmtTime(dur)}</div>
            <input value={bookmarkLabel}
              onChange={e=>onBookmarkLabelChange(e.target.value)}
              placeholder="Label…" autoFocus
              onKeyDown={e=>{
                if(e.key==="Enter") onBookmarkSave();
                if(e.key==="Escape") onBookmarkCancel();
              }}
              style={{ width:"100%", padding:"9px 12px", borderRadius:"8px",
                border:"1px solid rgba(255,255,255,.2)", background:"#111",
                color:"#fff", fontSize:"13px", fontFamily:"inherit",
                outline:"none", boxSizing:"border-box", marginBottom:"12px" }}/>
            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={onBookmarkCancel}
                style={{ flex:1, padding:"9px", borderRadius:"8px",
                  border:"1px solid rgba(255,255,255,.15)",
                  background:"rgba(255,255,255,.08)", color:"#fff",
                  cursor:"pointer", fontFamily:"inherit", fontSize:"13px" }}>
                Cancel</button>
              <button onClick={onBookmarkSave} disabled={!bookmarkLabel.trim()}
                style={{ flex:1, padding:"9px", borderRadius:"8px", border:"none",
                  fontFamily:"inherit", fontSize:"13px", fontWeight:"700",
                  cursor:bookmarkLabel.trim()?"pointer":"not-allowed",
                  background:bookmarkLabel.trim()?"#7C3AED":"#333",
                  color:bookmarkLabel.trim()?"#fff":"#666" }}>
                📸 Save</button>
            </div>
          </div>
        </div>
      )}

      {/* toolbar */}
      <div style={{ flexShrink:0, display:"flex", alignItems:"center",
        gap:"6px", padding:"8px 14px",
        background:"rgba(0,0,0,.95)",
        borderBottom:"1px solid rgba(255,255,255,.1)", flexWrap:"wrap" }}>

        {/* timer */}
        <div style={{ display:"flex", alignItems:"center", gap:"6px",
          background:"rgba(239,68,68,.15)", borderRadius:"20px", padding:"4px 12px" }}>
          <span style={{ width:"9px", height:"9px", borderRadius:"50%",
            background:recording?"#EF4444":"#6B7280", flexShrink:0,
            animation:recording?"blink 1s infinite":"none" }}/>
          <span style={{ color:"#fff", fontFamily:"monospace", fontSize:"14px",
            fontWeight:"700", minWidth:"46px" }}>{fmtTime(dur)}</span>
        </div>

        {recError && (
          <span style={{ fontSize:"11px", color:"#FCA5A5",
            background:"rgba(239,68,68,.15)", padding:"3px 10px",
            borderRadius:"6px", maxWidth:"220px" }}>⚠ {recError}</span>
        )}

        <div style={{ width:"1px", height:"22px", background:"rgba(255,255,255,.15)" }}/>
        <TB bg="rgba(34,197,94,.2)"  col="#4ADE80" onClick={onScreenshot}>📸 Screenshot</TB>
        <TB bg="rgba(168,85,247,.2)" col="#C084FC" onClick={onBookmarkOpen}>🔖 Bookmark</TB>
        <TB bg="rgba(59,130,246,.2)" col="#60A5FA"
          onClick={onSelectArea}>
          {selArea ? "📐 Reselect Area" : "📐 Select Area"}
        </TB>
        {selArea && <TB bg="rgba(255,255,255,.1)" col="#9CA3AF" onClick={onClearArea}>✕ Full Frame</TB>}
        <div style={{ flex:1 }}/>
        <span style={{ fontSize:"11px", color:"#9CA3AF" }}>{screenshotCount} captures</span>
        <div style={{ width:"1px", height:"22px", background:"rgba(255,255,255,.15)" }}/>

        {/* rec controls — Rec toggles pause/resume, Exit stops */}
        {!recording && !paused && (
          <TB bg="rgba(239,68,68,.2)" col="#FCA5A5" onClick={onRec} disabled={starting}>
            {starting ? "Starting…" : "▶ Rec"}
          </TB>
        )}
        {recording && (
          <TB bg="rgba(251,191,36,.2)" col="#FCD34D" onClick={onPause}>⏸ Pause</TB>
        )}
        {paused && (
          <TB bg="rgba(34,197,94,.2)" col="#4ADE80" onClick={onResume}>▶ Resume</TB>
        )}

        <TB
          bg={micMuted?"rgba(255,255,255,.08)":"rgba(34,197,94,.2)"}
          col={micMuted?"#6B7280":"#4ADE80"}
          onClick={onToggleMic}
          title={micMuted?"Mic off (click to unmute)":"Mic on (click to mute)"}>
          {micMuted ? "🔇 Mic" : "🎤 Mic"}
        </TB>
        <div style={{ width:"1px", height:"22px", background:"rgba(255,255,255,.15)" }}/>
        <TB bg="rgba(255,255,255,.1)" col="#9CA3AF"
          onClick={()=>document.fullscreenElement
            ? document.exitFullscreen()
            : document.documentElement.requestFullscreen?.().catch(()=>{})}>
          ⛶ Full
        </TB>
        {/* Generate Transcript mid-recording */}
        {user&&(recording||paused)&&(
          <button onClick={async ()=>{
              setTxLoading(true); setTxErr("");
              try {
                const blob = await getChunkBlob?.();
                if(!blob||blob.size===0){ setTxErr("No audio yet."); setTxLoading(false); return; }
                const ext = blob.type.includes("mp4")?"mp4":blob.type.includes("wav")?"wav":"webm";
                const fd = new FormData();
                fd.append("userid", user.USERID);
                fd.append("file", blob, `recording.${ext}`);
                const r = await fetch("https://cip.medsphere.net/api/transcribe",
                  {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
                const res = await r.json();
                if(String(res.STATUS)==="1"){
                  // auto-download Word immediately
                  const txText = res.TRANSCRIPT||res.PLAIN_TEXT||"";
                  const escXml=s=>(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
                  const now=new Date(); const pad=n=>String(n).padStart(2,"0");
                  const ts=`${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
                  const lines=txText.split("\n").filter(l=>l.trim());
                  const paras=lines.map(line=>{
                    const m=line.match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/i);
                    if(m) return `<w:p><w:pPr><w:spacing w:before="120" w:after="60"/></w:pPr><w:r><w:rPr><w:b/><w:color w:val="E67E22"/></w:rPr><w:t xml:space="preserve">${escXml(m[1].trim())} </w:t></w:r><w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t>${escXml(m[2].trim())}</w:t></w:r></w:p>`;
                    return `<w:p><w:pPr><w:spacing w:after="60"/></w:pPr><w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t xml:space="preserve">${escXml(line)}</w:t></w:r></w:p>`;
                  });
                  // RTF only — works on local and server, no JSZip needed
                  const escRtf=s=>(s||"").replace(/\\/g,"\\\\").replace(/\{/g,"\\{").replace(/\}/g,"\\}").replace(/[^\x00-\x7F]/g,c=>`\\u${c.charCodeAt(0)}\\'3f`);
                  let body="";
                  lines.forEach(line=>{
                    const m=line.match(/^(SPEAKER\s+[A-Z]:\s*)(.*)/i);
                    if(m) body+=`{\\b\\cf1 ${escRtf(m[1].trim())}}\\b0\\cf0  ${escRtf(m[2].trim())}\\par\\par\n`;
                    else  body+=escRtf(line)+"\\par\n";
                  });
                  const rtf=`{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0\\fswiss Arial;}}{\\colortbl;\\red230\\green126\\blue34;}\\f0\\fs24\n${body}}`;
                  const blob=new Blob([rtf],{type:"application/rtf"});
                  const url=URL.createObjectURL(blob); const a=document.createElement("a");
                  a.href=url; a.download=`Transcript_${ts}.rtf`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  setTimeout(()=>URL.revokeObjectURL(url),1000);
                } else setTxErr(res.MESSAGE||"Failed");
              } catch { setTxErr("Network error"); }
              setTxLoading(false);
            }}
            disabled={txLoading}
            style={{padding:"4px 10px",borderRadius:"6px",border:"none",cursor:txLoading?"not-allowed":"pointer",
              background:"rgba(99,102,241,.25)",color:"#A5B4FC",fontSize:"11px",fontWeight:"700",
              display:"flex",alignItems:"center",gap:"5px",opacity:txLoading?.7:1}}>
            {txLoading
              ? <><span style={{width:"9px",height:"9px",border:"2px solid #A5B4FC44",borderTopColor:"#A5B4FC",
                  borderRadius:"50%",animation:"spin .7s linear infinite",display:"inline-block"}}/>Transcribing…</>
              : "📝 Transcript"}
          </button>
        )}
        {txErr&&<span style={{fontSize:"10px",color:"#FCA5A5"}}>{txErr}</span>}
        <TB bg="rgba(239,68,68,.1)" col="#F87171" onClick={onExit}>✕ Exit</TB>
      </div>

      <div style={{ flex:1, position:"relative", overflow:"hidden",
        cursor:selectingArea||isSelecting?"crosshair":"default" }}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
        <video id="pvScreen" autoPlay muted playsInline
          style={{ width:"100%", height:"100%", objectFit:"contain",
            display:"block", pointerEvents:"none" }}/>

        {/* selection box */}
        {selArea && (
          <div style={{ position:"absolute", pointerEvents:"none",
            left:`${selArea.px*100}%`, top:`${selArea.py*100}%`,
            width:`${selArea.pw*100}%`, height:`${selArea.ph*100}%`,
            border:"2px solid #3B82F6",
            boxShadow:"0 0 0 9999px rgba(0,0,0,.5)" }}>
            <div style={{ position:"absolute", top:"-22px", left:0,
              background:"#3B82F6", color:"#fff", fontSize:"10px",
              fontWeight:"700", padding:"2px 7px", borderRadius:"4px",
              whiteSpace:"nowrap" }}>
              {Math.round(selArea.vw)}×{Math.round(selArea.vh)} px
            </div>
          </div>
        )}

        {/* captures strip — bottom right */}
        {screenshots.length > 0 && (
          <ScreenshotPanel screenshots={screenshots}
            onDownloadShot={onDownloadShot}
            onRemoveShot={onRemoveShot}/>
        )}
      </div>

      {/* hints */}
      <div style={{ flexShrink:0, display:"flex", gap:"12px", padding:"5px 14px",
        background:"rgba(0,0,0,.9)", borderTop:"1px solid rgba(255,255,255,.1)",
        fontSize:"11px", color:"rgba(255,255,255,.5)", alignItems:"center" }}>
        <span><b style={{color:"#fff"}}>📐</b> select area</span>
        <span><b style={{color:"#fff"}}>Space</b> screenshot</span>
        <span><b style={{color:"#fff"}}>B</b> bookmark</span>
        <span><b style={{color:"#fff"}}>R</b> rec/pause</span>
        <span><b style={{color:"#fff"}}>F</b> fullscreen</span>
        <span><b style={{color:"#fff"}}>Esc</b> exit</span>
        <div style={{flex:1}}/>
        <span style={{color:selArea?"#60A5FA":selectingArea?"#FCD34D":"#9CA3AF"}}>
          {selectingArea?"🎯 Drag to select — release to confirm":selArea
            ? `📐 ${Math.round(selArea.vw)}×${Math.round(selArea.vh)}px`
            : "🖥 Full frame"}
        </span>
      </div>

      <style>{"@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}"}</style>
    </div>,
    document.body
  );
}

// ─── useScreenCapture hook ────────────────────────────────────────────────────
export function useScreenCapture({ user } = {}) {
  const [active,      setActive]      = useState(false);
  const [recording,   setRecording]   = useState(false);
  const [starting,    setStarting]    = useState(false); // mic permission in progress
  const [paused,      setPaused]      = useState(false);
  const [dur,         setDur]         = useState(0);
  const [audioDur,    setAudioDur]    = useState(0); // final duration after stop
  const [audioBlob,   setAudioBlob]   = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [selArea,     setSelArea]     = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectingArea,   setSelectingArea]   = useState(false);
  const [bookmarkModal,   setBookmarkModal]   = useState(false);
  const [bookmarkLabel,   setBookmarkLabel]   = useState("");
  const [recError,    setRecError]    = useState("");
  const [micMuted,    setMicMuted]    = useState(true); // mic muted by default

  const streamRef      = useRef(null);
  const mrRef          = useRef(null);
  const chunksRef      = useRef([]);
  const timerRef       = useRef(null);
  const durRef         = useRef(0);
  const recordingRef   = useRef(false);
  const isSelectingRef = useRef(false);
  const selStartRef    = useRef(null);
  const selAreaRef     = useRef(null);

  // ── start screen share ──────────────────────────────────────────────────
  const start = async () => {
    setRecError("");
    try {
      const dpr = window.devicePixelRatio || 1;
      const s = await navigator.mediaDevices.getDisplayMedia({
        video:{
          // Request native pixel resolution — Chrome will honour this for tab capture
          width:       { ideal: window.screen.width  * dpr, max: 7680 },
          height:      { ideal: window.screen.height * dpr, max: 4320 },
          frameRate:   { ideal: 30, max: 60 },
          cursor:      "never",           // no cursor burnt into frames
          displaySurface: "browser",      // prefer tab capture over whole screen
          logicalSurface: true,
          resizeMode:  "none",            // don't let browser downsample
        },
        audio:{ echoCancellation:false, noiseSuppression:false,
          autoGainControl:false, sampleRate:44100,
          channelCount:2, suppressLocalAudioPlayback:false }
      });
      streamRef.current = s;
      durRef.current = 0;
      setDur(0); setAudioBlob(null); setSelArea(null);
      setActive(true);
      // attach stream to portal video after it mounts
      setTimeout(()=>{
        const v = document.getElementById("pvScreen");
        if(v){ v.srcObject=s; v.muted=true; v.play().catch(()=>{}); }
      }, 80);
      s.getVideoTracks()[0]?.addEventListener("ended", ()=>{ stopRec(); setTimeout(stop,350); });
    } catch(e) {
      setRecError(e.name==="NotAllowedError"?"Permission denied.":e.message);
    }
  };

  // ── exit ────────────────────────────────────────────────────────────────
  const stop = () => {
    streamRef.current?.getTracks().forEach(t=>t.stop());
    streamRef.current = null;
    const v = document.getElementById("pvScreen");
    if(v) v.srcObject = null;
    clearInterval(timerRef.current);
    if(mrRef.current?.state !== "inactive") {
      mrRef.current?.stop();
      setTimeout(()=>{ setActive(false); setRecording(false); setPaused(false); }, 350);
    } else {
      setActive(false); setRecording(false); setPaused(false);
    }
    recordingRef.current = false;
  };

  // ── start audio recording ────────────────────────────────────────────────
  const startRec = async () => {
    if(!streamRef.current) return;
    setRecError(""); setStarting(true);

    try {
      // Get mic — only if not muted
      let micAt = [];
      if(!micMuted){
        try {
          const mic = await navigator.mediaDevices.getUserMedia({
            audio:{ echoCancellation:true, noiseSuppression:true, sampleRate:44100 }
          });
          micAt = mic.getAudioTracks();
        } catch(e) {}
      }

      // System audio from the screen stream — clone tracks so video element keeps its copy
      const sysAt = streamRef.current.getAudioTracks()
        .filter(t=>t.readyState==="live")
        .map(t=>t.clone());

      let audioStream;
      if(sysAt.length && micAt.length) {
        // Mix via AudioContext
        const ctx = new AudioContext({ sampleRate:44100 });
        // Resume context (Chrome requires user gesture)
        await ctx.resume();
        const dest = ctx.createMediaStreamDestination();
        const sysNode = ctx.createMediaStreamSource(new MediaStream(sysAt));
        const micNode = ctx.createMediaStreamSource(new MediaStream(micAt));
        sysNode.connect(dest);
        micNode.connect(dest);
        audioStream = dest.stream;
      } else if(sysAt.length) {
        audioStream = new MediaStream(sysAt);
      } else if(micAt.length) {
        audioStream = new MediaStream(micAt);
      } else {
        setRecError("No audio. Enable 'Share tab audio' when sharing, or allow microphone.");
        setStarting(false); return;
      }

      // Verify tracks are live
      const liveTracks = audioStream.getAudioTracks().filter(t=>t.readyState==="live");
      if(!liveTracks.length) {
        setRecError("Audio tracks ended. Try sharing again with audio enabled.");
        setStarting(false); return;
      }

      chunksRef.current = [];
      const fmt = bestAudioFmt();
      let mr;
      try   { mr = new MediaRecorder(audioStream, fmt.mimeType ? fmt : {}); }
      catch { mr = new MediaRecorder(audioStream); }

      // ── Wake Lock — prevent browser throttling during long recordings ──────
      let wakeLock = null;
      const acquireWakeLock = async () => {
        try {
          if("wakeLock" in navigator) {
            wakeLock = await navigator.wakeLock.request("screen");
            wakeLock.addEventListener("release", ()=>{ wakeLock=null; });
          }
        } catch {}
      };
      acquireWakeLock();

      // Re-acquire wake lock if tab becomes visible again (mobile/browser may release it)
      const onVisChange = () => {
        if(document.visibilityState==="visible" && wakeLock===null) acquireWakeLock();
      };
      document.addEventListener("visibilitychange", onVisChange);

      mr.ondataavailable = e => {
        if(e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onerror = () => {};
      mr.onstop = () => {
        // Release wake lock
        try { wakeLock?.release(); } catch {}
        document.removeEventListener("visibilitychange", onVisChange);
        const b = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const finalDur = durRef.current;
        setAudioBlob(b);
        setAudioDur(finalDur);
        setRecording(false); recordingRef.current = false; setPaused(false);
        clearInterval(timerRef.current);

      };

      // Use 5s timeslice for long recordings — fewer chunks, less memory pressure
      mr.start(5000);
      mrRef.current = mr;
      durRef.current = 0; setDur(0);
      setRecording(true); recordingRef.current = true; setPaused(false);
      timerRef.current = setInterval(()=>{ durRef.current++; setDur(d=>d+1); }, 1000);
    } catch(e) {
      setRecError(e.message||"Failed to start recording.");
    }
    setStarting(false);
  };

  // ── auto-start recording when screen share becomes active ────────────────
  const autoStartedRef = useRef(false);
  useEffect(()=>{
    if(active && !recording && !paused && !autoStartedRef.current){
      autoStartedRef.current = true;
      startRec();
    }
    if(!active) autoStartedRef.current = false;
  },[active]);

  const pauseRec  = () => {
    mrRef.current?.pause();
    setPaused(true); setRecording(false); recordingRef.current = false;
    clearInterval(timerRef.current);
  };
  const resumeRec = () => {
    mrRef.current?.resume();
    setPaused(false); setRecording(true); recordingRef.current = true;
    timerRef.current = setInterval(()=>{ durRef.current++; setDur(d=>d+1); }, 1000);
  };
  const stopRec = () => {
    if(mrRef.current?.state !== "inactive") {
      // request final data before stopping
      mrRef.current.requestData?.();
      mrRef.current.stop();
    }
    clearInterval(timerRef.current);

    // ── auto-download — must be in user gesture (button click) context ───────
    // build blob from current chunks immediately (before onstop fires async)
    setTimeout(()=>{
      try {
        const currentChunks = chunksRef.current;
        if(!currentChunks.length) return;
        const mimeType = mrRef.current?.mimeType||"audio/webm";
        const b   = new Blob(currentChunks, {type:mimeType});
        const url = URL.createObjectURL(b);
        const a   = document.createElement("a");
        const ts  = new Date().toISOString().replace(/[:.]/g,"-").slice(0,19);
        a.href    = url;
        a.download= `echo_recording_${ts}.webm`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(()=>URL.revokeObjectURL(url), 2000);
      } catch(e){}
    }, 200); // small delay for requestData to flush
  };

  // ── screenshot ───────────────────────────────────────────────────────────
  const takeScreenshot = async (isBookmark=false, label="") => {
    const v = document.getElementById("pvScreen");
    if(!v || !v.videoWidth) return;

    const area = selAreaRef.current;
    const dpr  = window.devicePixelRatio || 1;

    // ── 1. Grab full-res frame via ImageCapture (bypasses GPU downsampling) ──
    let frameBitmap = null;
    try {
      const track = streamRef.current?.getVideoTracks()[0];
      if(track && typeof ImageCapture !== "undefined") {
        const ic = new ImageCapture(track);
        frameBitmap = await ic.grabFrame();
      }
    } catch(e) {
    }

    const nativeW = frameBitmap ? frameBitmap.width  : v.videoWidth;
    const nativeH = frameBitmap ? frameBitmap.height : v.videoHeight;
    const source  = frameBitmap || v;

    // ── 2. Compute crop region ────────────────────────────────────────────
    let sx=0, sy=0, sw=nativeW, sh=nativeH;
    if(area) {
      const vr  = v.getBoundingClientRect();
      const dAR = vr.width / vr.height;
      const vAR = nativeW  / nativeH;
      let rendW, rendH, offX=0, offY=0;
      if(dAR > vAR) { rendH=vr.height; rendW=rendH*vAR; offX=(vr.width-rendW)/2; }
      else           { rendW=vr.width;  rendH=rendW/vAR; offY=(vr.height-rendH)/2; }
      const ax = (area.px*vr.width  - offX) / rendW;
      const ay = (area.py*vr.height - offY) / rendH;
      const aw = area.pw*vr.width  / rendW;
      const ah = area.ph*vr.height / rendH;
      sx = Math.max(0, Math.round(ax * nativeW));
      sy = Math.max(0, Math.round(ay * nativeH));
      sw = Math.min(nativeW - sx, Math.round(aw * nativeW));
      sh = Math.min(nativeH - sy, Math.round(ah * nativeH));
    }

    // ── 3. Upscale to DPR× if capture is at logical resolution ───────────
    // Chrome captures at logical (CSS) pixels — upscale to physical pixels
    // using high-quality bicubic resampling so charts/graphs stay sharp
    let scaledBitmap = null;
    const targetW = Math.round(sw * dpr);
    const targetH = Math.round(sh * dpr);
    const needsUpscale = dpr > 1 && (targetW > sw || targetH > sh);

    if(needsUpscale) {
      try {
        // createImageBitmap with resizeQuality:"high" uses bicubic — best for charts
        scaledBitmap = await createImageBitmap(source,
          sx, sy, sw, sh,
          { resizeWidth: targetW, resizeHeight: targetH, resizeQuality: "high" }
        );
      } catch(e) {
      }
    }

    const barH   = isBookmark && label ? Math.round(32 * (needsUpscale ? dpr : 1)) : 0;
    const outW   = scaledBitmap ? targetW : sw;
    const outH   = scaledBitmap ? targetH : sh;
    const drawSrc = scaledBitmap || source;

    const canvas = document.createElement("canvas");
    canvas.width  = outW;
    canvas.height = outH + barH;
    const ctx = canvas.getContext("2d", { alpha:false });
    ctx.imageSmoothingEnabled = false;   // no further blurring after our upscale
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if(scaledBitmap) {
      // already upscaled — draw 1:1
      ctx.drawImage(drawSrc, 0, 0);
    } else {
      // no upscale — draw crop 1:1
      ctx.drawImage(drawSrc, sx, sy, sw, sh, 0, 0, outW, outH);
    }

    // free GPU memory
    if(frameBitmap)  frameBitmap.close();
    if(scaledBitmap) scaledBitmap.close();

    const t = fmtTime(durRef.current);
    if(isBookmark && label) {
      const fontSize = Math.max(13, Math.round(outW / 80));
      ctx.fillStyle = "rgba(0,0,0,.85)";
      ctx.fillRect(0, outH, outW, barH);
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${fontSize}px -apple-system,sans-serif`;
      ctx.fillText(`🔖 ${label}`, 12, outH + barH * 0.72);
    }

    // flash
    const fl = document.getElementById("pvFlash");
    if(fl){ fl.style.opacity="0.8"; setTimeout(()=>{ if(fl) fl.style.opacity="0"; }, 180); }

    canvas.toBlob(blob => {
      if(!blob) return;
      const fname = `${isBookmark?"bm":"ss"}_${Date.now()}.png`;
      const file  = new File([blob], fname, { type:"image/png" });
      setScreenshots(p => [...p, { file, screentime:t, isBookmark, bookmarkText:isBookmark?label:"" }]);
      // auto-download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(url), 1000);
    }, "image/png", 1.0);
  };

  const downloadShot = ss => {
    const url = URL.createObjectURL(ss.file);
    const a = document.createElement("a");
    a.href = url; a.download = ss.file.name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
  };

  // ── mouse selection ──────────────────────────────────────────────────────
  const onMouseDown = e => {
    if(!selectingArea) return; // only draw when in select mode
    const v = document.getElementById("pvScreen"); if(!v) return;
    const vr = v.getBoundingClientRect();
    isSelectingRef.current = true; setIsSelecting(true); setSelArea(null); selAreaRef.current=null;
    selStartRef.current = {
      px: (e.clientX-vr.left)/vr.width,
      py: (e.clientY-vr.top)/vr.height
    };
  };
  const onMouseMove = e => {
    if(!isSelectingRef.current || !selStartRef.current) return;
    const v = document.getElementById("pvScreen"); if(!v) return;
    const vr = v.getBoundingClientRect();
    const ex = Math.max(0, Math.min(1, (e.clientX-vr.left)/vr.width));
    const ey = Math.max(0, Math.min(1, (e.clientY-vr.top)/vr.height));
    const { px:sx, py:sy } = selStartRef.current;
    const x=Math.min(sx,ex), y=Math.min(sy,ey);
    const w=Math.abs(ex-sx), h=Math.abs(ey-sy);
    if(w>0.01 && h>0.01) setSelArea({
      px:x, py:y, pw:w, ph:h,
      vx:x*v.videoWidth,  vy:y*v.videoHeight,
      vw:w*v.videoWidth,  vh:h*v.videoHeight
    });
    selAreaRef.current = {
      px:x, py:y, pw:w, ph:h,
      vx:x*v.videoWidth, vy:y*v.videoHeight,
      vw:w*v.videoWidth, vh:h*v.videoHeight
    };
  };
  const onMouseUp = () => { isSelectingRef.current=false; setIsSelecting(false); setSelectingArea(false); };

  // ── keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(()=>{
    const h = e => {
      if(!active) return;
      if(e.target.tagName==="INPUT" || e.target.tagName==="TEXTAREA") return;
      switch(e.code){
        case "Space": e.preventDefault(); takeScreenshot(false); break;
        case "KeyB":
          e.preventDefault();
          setBookmarkModal(m=>{ if(!m) setBookmarkLabel(""); return !m; });
          break;
        case "KeyR":
          e.preventDefault();
          if(recordingRef.current) pauseRec();       // rec → pause
          else if(mrRef.current?.state==="paused") resumeRec(); // pause → rec
          else startRec();                            // idle → rec
          break;
        case "KeyF":
          e.preventDefault();
          document.fullscreenElement
            ? document.exitFullscreen()
            : document.documentElement.requestFullscreen?.().catch(()=>{});
          break;
        case "Escape":
          e.preventDefault();
          if(bookmarkModal) setBookmarkModal(false);
          else if(document.fullscreenElement) document.exitFullscreen();
          else stop();
          break;
        default: break;
      }
    };
    window.addEventListener("keydown", h);
    return ()=>window.removeEventListener("keydown", h);
  }, [active, bookmarkModal]);

  // ── render overlay ───────────────────────────────────────────────────────
  const overlay = active ? (
    <ScreenOverlay
      dur={dur} recording={recording} paused={paused} starting={starting}
      micMuted={micMuted} onToggleMic={()=>setMicMuted(m=>!m)}
      screenshotCount={screenshots.length} selArea={selArea} isSelecting={isSelecting}
      selectingArea={selectingArea}
      recError={recError} bookmarkModal={bookmarkModal} bookmarkLabel={bookmarkLabel}
      screenshots={screenshots}
      onRec={startRec} onPause={pauseRec} onResume={resumeRec} onStop={stopRec}
      onExit={()=>{ stopRec(); setTimeout(stop,350); }}
      onSelectArea={()=>{ setSelArea(null); selAreaRef.current=null; setSelectingArea(true); }}
      onScreenshot={()=>takeScreenshot(false)}
      onBookmarkOpen={()=>{ setBookmarkModal(true); setBookmarkLabel(""); }}
      onBookmarkSave={()=>{
        if(bookmarkLabel.trim()){
          takeScreenshot(true, bookmarkLabel.trim());
          setBookmarkModal(false); setBookmarkLabel("");
        }
      }}
      onBookmarkCancel={()=>setBookmarkModal(false)}
      onBookmarkLabelChange={setBookmarkLabel}
      onClearArea={()=>{ setSelArea(null); selAreaRef.current=null; }}
      onRemoveShot={i=>setScreenshots(p=>p.filter((_,j)=>j!==i))}
      onDownloadShot={downloadShot}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
      user={user}
      getChunkBlob={()=>new Promise(resolve=>{
        const mr=mrRef.current;
        if(mr&&mr.state!=="inactive"){
          const prev=mr.ondataavailable;
          mr.ondataavailable=e=>{
            if(e.data.size>0) chunksRef.current.push(e.data);
            mr.ondataavailable=prev;
            resolve(chunksRef.current.length>0
              ? new Blob(chunksRef.current,{type:mr.mimeType||"audio/webm"})
              : null);
          };
          try{ mr.requestData(); }catch{
            mr.ondataavailable=prev;
            resolve(chunksRef.current.length>0?new Blob(chunksRef.current,{type:mr.mimeType||"audio/webm"}):null);
          }
        } else {
          resolve(chunksRef.current.length>0?new Blob(chunksRef.current,{type:"audio/webm"}):null);
        }
      })}/>
  ) : null;

  return { active, recording, paused, starting, dur, audioDur, audioBlob, screenshots, overlay, start, stop, downloadShot, micMuted };
}